import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';
import { api } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingScreen';
import { AppIcon } from '@/components/AppIcon';

interface InviteInfo {
  id: string;
  name: string;
  memberCount: number;
}

export default function FamilySetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setFamilyId, familyId, user, updateDisplayName } = useAuthStore();
  const { showToast } = useAppStore();

  // addNew=true の場合はグループ追加モード（既存グループがあっても表示）
  const isAddNewMode = searchParams.get('addNew') === 'true';
  const codeFromUrl = searchParams.get('code');

  const [mode, setMode] = useState<'select' | 'create' | 'join' | 'invite-confirm' | 'auto-joining'>('select');
  const [familyName, setFamilyName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [createdInviteCode, setCreatedInviteCode] = useState<string | null>(null);
  const [createdFamilyId, setCreatedFamilyId] = useState<string | null>(null);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [isLoadingInvite, setIsLoadingInvite] = useState(false);
  const inviteChecked = useRef(false);
  // 表示名（10文字制限、Googleアカウント名を切り詰め）
  const [displayName, setDisplayName] = useState('');

  // URLから招待コードを取得してグループ情報を表示
  useEffect(() => {
    if (codeFromUrl && codeFromUrl.length === 6 && !inviteChecked.current) {
      inviteChecked.current = true;
      loadInviteInfo(codeFromUrl.toUpperCase());
    }
  }, [codeFromUrl]);

  // 既に家族に所属していて追加モードでない場合はホームへ
  useEffect(() => {
    if (familyId && !isAddNewMode) {
      navigate('/', { replace: true });
    }
  }, [familyId, isAddNewMode, navigate]);

  // 招待コードからグループ情報を取得
  const loadInviteInfo = async (code: string) => {
    setIsLoadingInvite(true);
    try {
      const info = await api.getInviteInfo(code);
      setInviteInfo(info);
      setInviteCode(code);
      // Googleアカウント名を初期値に設定（10文字まで切り詰め）
      if (user?.displayName) {
        setDisplayName(user.displayName.slice(0, 10));
      }
      setMode('invite-confirm');
    } catch (error: any) {
      // 無効な招待コードの場合は通常の選択画面へ
      showToast(error.message || '無効な招待コードです', 'error');
      setMode('select');
    } finally {
      setIsLoadingInvite(false);
    }
  };

  // 招待確認画面から参加
  const handleConfirmJoin = async () => {
    if (!inviteCode) return;
    if (!displayName.trim()) {
      showToast('名前を入力してください', 'error');
      return;
    }

    setIsLoading(true);
    try {
      // 表示名を更新
      await api.updateUserProfile({ displayName: displayName.trim() });
      updateDisplayName(displayName.trim());

      const result = await api.joinFamily({ inviteCode });
      setFamilyId(result.familyId);
      showToast('グループに参加しました！', 'success');
      navigate('/', { replace: true });
    } catch (error: any) {
      showToast(error.message || '参加に失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      showToast('グループ名を入力してください', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const result = await api.createFamily({ name: familyName });
      setCreatedInviteCode(result.inviteCode);
      setCreatedFamilyId(result.id);
      setFamilyId(result.id);
    } catch (error: any) {
      showToast(error.message || 'グループの作成に失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinFamily = async () => {
    if (inviteCode.length !== 6) {
      showToast('招待コードは6文字です', 'error');
      return;
    }

    setIsLoading(true);
    try {
      const result = await api.joinFamily({ inviteCode: inviteCode.toUpperCase() });
      setFamilyId(result.familyId);
      showToast('グループに参加しました', 'success');
      navigate('/', { replace: true });
    } catch (error: any) {
      showToast(error.message || '参加に失敗しました', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    navigate('/', { replace: true });
  };

  const handleBack = () => {
    if (isAddNewMode) {
      navigate('/settings/group');
    } else {
      setMode('select');
      setInviteInfo(null);
      setInviteCode('');
    }
  };

  // 招待URLを生成
  const getInviteUrl = (code: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite?code=${code}`;
  };

  // 招待URLを共有
  const handleShareInvite = async (code: string) => {
    const inviteUrl = getInviteUrl(code);
    const shareText = `シェア家計簿に招待されました！\n以下のリンクから参加してください：\n${inviteUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'シェア家計簿への招待',
          text: shareText,
          url: inviteUrl,
        });
      } catch (error) {
        console.log('Share cancelled');
      }
    } else {
      await navigator.clipboard.writeText(inviteUrl);
      showToast('招待URLをコピーしました', 'success');
    }
  };

  // 招待情報読み込み中
  if (isLoadingInvite) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
        <div className="w-full max-w-sm text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">招待情報を確認中...</p>
        </div>
      </div>
    );
  }

  // 招待確認画面
  if (mode === 'invite-confirm' && inviteInfo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
        <div className="w-full max-w-sm">
          <div className="card p-6 text-center">
            <div className="empty-icon mx-auto mb-4"><AppIcon name="users" size={22} /></div>
            <h2 className="text-xl font-bold mb-2">グループへの招待</h2>
            <p className="text-gray-600 mb-6">
              以下のグループに招待されています
            </p>

            <div className="bg-primary-50 rounded-lg p-4 mb-6">
              <p className="text-2xl font-bold text-primary-700 mb-1">
                {inviteInfo.name}
              </p>
              <p className="text-sm text-gray-500">
                現在のメンバー: {inviteInfo.memberCount}人
              </p>
            </div>

            {/* 名前入力フィールド */}
            <div className="mb-6 text-left">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                あなたの名前（10文字まで）
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.slice(0, 10))}
                placeholder="名前を入力"
                className="input"
                maxLength={10}
              />
              <p className="mt-1 text-xs text-gray-500">
                グループ内で表示される名前です
              </p>
            </div>

            <button
              onClick={handleConfirmJoin}
              disabled={isLoading || !displayName.trim()}
              className="btn btn-primary btn-lg w-full mb-3"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : '参加する'}
            </button>

            <button
              onClick={handleBack}
              className="btn btn-ghost btn-md w-full"
            >
              キャンセル
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 招待コード表示画面
  if (createdInviteCode && createdFamilyId) {
    const inviteUrl = getInviteUrl(createdInviteCode);

    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
        <div className="w-full max-w-sm">
          <div className="card p-6 text-center">
            <div className="empty-icon mx-auto mb-4"><AppIcon name="check" size={22} /></div>
            <h2 className="text-xl font-bold mb-2">グループを作成しました</h2>
            <p className="text-gray-600 mb-6">
              パートナーに招待リンクを共有してください
            </p>

            {/* 招待URL */}
            <div className="bg-gray-100 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-500 mb-2">招待リンク</p>
              <p className="text-sm font-mono break-all text-primary-600">
                {inviteUrl}
              </p>
            </div>

            {/* 共有ボタン */}
            <button
              onClick={() => handleShareInvite(createdInviteCode)}
              className="btn btn-primary btn-lg w-full mb-3"
            >
              <AppIcon name="share" size={18} />
              招待リンクを共有
            </button>

            {/* コピーボタン */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(inviteUrl);
                showToast('招待URLをコピーしました', 'success');
              }}
              className="btn btn-secondary btn-md w-full mb-6"
            >
              <AppIcon name="copy" size={18} />
              URLをコピー
            </button>

            <div className="border-t border-gray-200 pt-4">
              <p className="text-xs text-gray-500 mb-2">招待コード</p>
              <p className="text-2xl font-mono font-bold tracking-widest text-gray-600 mb-4">
                {createdInviteCode}
              </p>
            </div>

            <button
              onClick={handleContinue}
              className="btn btn-ghost btn-md w-full"
            >
              あとで招待する →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // 自動参加中の画面
  if (mode === 'auto-joining') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
        <div className="w-full max-w-sm text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">グループに参加しています...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gray-50">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-2">
            {isAddNewMode ? 'グループを追加' : 'グループを設定'}
          </h1>
          <p className="text-gray-600">
            {isAddNewMode
              ? '新しいグループを作成するか、招待リンクで参加してください'
              : '共有グループを作成するか、招待リンクで参加してください'
            }
          </p>
        </div>

        {mode === 'select' && (
          <div className="space-y-4">
            {isAddNewMode && (
              <button
                onClick={() => navigate('/settings/group')}
                className="text-gray-500 mb-2 flex items-center gap-1"
              >
                ← 戻る
              </button>
            )}

            <button
              onClick={() => setMode('create')}
              className="card w-full p-6 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="section-icon section-icon-blue"><AppIcon name="plus" size={20} /></div>
                <div>
                  <h3 className="font-semibold mb-1">新しく作成する</h3>
                  <p className="text-sm text-gray-500">
                    グループを作成して、パートナーを招待
                  </p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setMode('join')}
              className="card w-full p-6 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="section-icon section-icon-mint"><AppIcon name="link" size={20} /></div>
                <div>
                  <h3 className="font-semibold mb-1">招待リンクで参加</h3>
                  <p className="text-sm text-gray-500">
                    パートナーから共有された招待で参加
                  </p>
                </div>
              </div>
            </button>
          </div>
        )}

        {mode === 'create' && (
          <div className="card p-6">
            <button
              onClick={handleBack}
              className="text-gray-500 mb-4 flex items-center gap-1"
            >
              ← 戻る
            </button>

            <h2 className="text-lg font-semibold mb-4">グループを作成</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                グループ名
              </label>
              <input
                type="text"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="例: 田中家、シェアハウス"
                className="input"
                maxLength={30}
              />
              <p className="mt-1 text-xs text-gray-500">
                後から変更できます
              </p>
            </div>

            <button
              onClick={handleCreateFamily}
              disabled={isLoading || !familyName.trim()}
              className="btn btn-primary btn-lg w-full"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : '作成する'}
            </button>
          </div>
        )}

        {mode === 'join' && (
          <div className="card p-6">
            <button
              onClick={handleBack}
              className="text-gray-500 mb-4 flex items-center gap-1"
            >
              ← 戻る
            </button>

            <h2 className="text-lg font-semibold mb-4">グループに参加</h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                招待コード（6文字）
              </label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="XXXXXX"
                className="input text-center text-2xl font-mono tracking-widest"
                maxLength={6}
              />
              <p className="mt-2 text-xs text-gray-500">
                招待リンクからアクセスした場合は自動入力されます
              </p>
            </div>

            <button
              onClick={handleJoinFamily}
              disabled={isLoading || inviteCode.length !== 6}
              className="btn btn-primary btn-lg w-full"
            >
              {isLoading ? <LoadingSpinner size="sm" /> : '参加する'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
